import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Link, useNavigate } from 'react-router-dom';
import { Mail, Lock, Eye, EyeOff, ArrowRight, Shield, Zap, Globe } from 'lucide-react';
import toast from 'react-hot-toast';
import useAuthStore from '../store/authStore';

const P  = '#C29500';
const P2 = '#E8C040';
const C  = '#06B6D4';
const G  = '#E8C040';

const KudiLogo = ({ size = 36 }) => (
  <svg width={size} height={size} viewBox="0 0 80 80" fill="none" xmlns="http://www.w3.org/2000/svg">
    <defs>
      <linearGradient id="kl-g" x1="0" y1="0" x2="80" y2="80" gradientUnits="userSpaceOnUse">
        <stop offset="0%" stopColor={P2}/>
        <stop offset="100%" stopColor={P}/>
      </linearGradient>
    </defs>
    <rect width="80" height="80" rx="20" fill="url(#kl-g)"/>
    <rect x="20" y="18" width="9" height="44" rx="4.5" fill="#fff"/>
    <path d="M29 40 L54 20" stroke="#fff" strokeWidth="9" strokeLinecap="round"/>
    <path d="M29 40 L54 60" stroke="#fff" strokeWidth="9" strokeLinecap="round"/>
    <path d="M42 56 L54 60 L50 48" stroke="#fff" strokeWidth="5" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

export default function Login() {
  const [form, setForm]         = useState({ email: '', password: '' });
  const [showPass, setShowPass] = useState(false);
  const [otpStep, setOtpStep]   = useState(false);
  const [otpEmail, setOtpEmail] = useState('');
  const [otp, setOtp]           = useState('');
  const [resendIn, setResendIn] = useState(0);
  const { login, verifyOtp, resendOtp, isLoading } = useAuthStore();
  const navigate                = useNavigate();

  React.useEffect(() => {
    if (resendIn <= 0) return;
    const t = setTimeout(() => setResendIn(s => s - 1), 1000);
    return () => clearTimeout(t);
  }, [resendIn]);

  const handleResend = async () => {
    if (resendIn > 0) return;
    const r = await resendOtp(otpEmail);
    if (r.success) { toast.success('A new code was sent.'); setOtp(''); setResendIn(30); }
    else toast.error(r.error);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const result = await login(form.email, form.password);
    if (result.requireOtp) {
      setOtpEmail(result.email);
      setOtpStep(true);
      setResendIn(30);
      toast.success('A login code was sent to your email.');
    } else if (result.success) {
      toast.success('Welcome back!');
      navigate('/dashboard');
    } else {
      toast.error(result.error);
    }
  };

  const handleVerify = async (e) => {
    e.preventDefault();
    if (otp.trim().length < 6) { toast.error('Enter the 6-digit code'); return; }
    const result = await verifyOtp(otpEmail, otp.trim());
    if (result.success) {
      toast.success('Welcome back!');
      navigate('/dashboard');
    } else {
      toast.error(result.error);
    }
  };

  return (
    <div style={{ minHeight: '100vh', background: '#060A18', display: 'flex', fontFamily: 'Inter, sans-serif' }}>

      {/* ── LEFT PANEL ── */}
      <div style={{
        display: 'none', flex: '0 0 50%', maxWidth: '50%', position: 'relative',
        overflow: 'hidden', flexDirection: 'column', alignItems: 'center',
        justifyContent: 'center', padding: 64,
        background: 'linear-gradient(135deg, rgba(194,149,0,0.06) 0%, rgba(6,182,212,0.04) 100%)',
      }} className="lg-panel">

        {/* Glow blobs */}
        <div style={{ position: 'absolute', top: '-10%', left: '-10%', width: 420, height: 420,
          background: `radial-gradient(circle, ${P}20 0%, transparent 65%)`, borderRadius: '50%', pointerEvents: 'none' }}/>
        <div style={{ position: 'absolute', bottom: '-10%', right: '-10%', width: 320, height: 320,
          background: `radial-gradient(circle, ${C}15 0%, transparent 65%)`, borderRadius: '50%', pointerEvents: 'none' }}/>
        <div style={{ position: 'absolute', inset: 0, opacity: 0.02,
          backgroundImage: `linear-gradient(${P} 1px, transparent 1px), linear-gradient(90deg, ${P} 1px, transparent 1px)`,
          backgroundSize: '56px 56px', pointerEvents: 'none' }}/>

        <div style={{ position: 'relative', zIndex: 1, textAlign: 'center', width: '100%', maxWidth: 400 }}>

          {/* Logo */}
          <motion.div initial={{ opacity: 0, y: -20 }} animate={{ opacity: 1, y: 0 }}
            style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 12, marginBottom: 48 }}>
            <div style={{ filter: `drop-shadow(0 0 16px ${P}60)` }}><KudiLogo size={48} /></div>
            <span style={{ fontSize: 32, fontWeight: 900, letterSpacing: -1, color: '#fff' }}>
              Kudi<span style={{ color: P2 }}>bot</span>
            </span>
          </motion.div>

          {/* Headline */}
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}>
            <h2 style={{ fontSize: 36, fontWeight: 900, marginBottom: 12, lineHeight: 1.1, color: '#fff', letterSpacing: -1 }}>
              Collect Stablecoins.<br/>
              <span style={{
                background: `linear-gradient(135deg, ${P2}, ${C})`,
                WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text',
              }}>Pay Out Globally.</span>
            </h2>
            <p style={{ color: '#64748B', fontSize: 16, marginBottom: 40, lineHeight: 1.6 }}>
              Accept USDT & USDC from anywhere. Settle to banks and mobile wallets across Africa in minutes.
            </p>
          </motion.div>

          {/* Features */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12, marginBottom: 40 }}>
            {[
              { icon: <Globe size={18}/>,  text: 'USDT & USDC collection via TRC20, ERC20, BEP20', color: P  },
              { icon: <Shield size={18}/>, text: 'Bank & Mobile Money payouts in NGN, GHS, KES+',   color: C  },
              { icon: <Zap size={18}/>,    text: 'Funds settle in 1–5 minutes, every time',          color: G  },
            ].map((f, i) => (
              <motion.div key={i}
                initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.2 + i * 0.1 }}
                style={{
                  display: 'flex', alignItems: 'center', gap: 14, padding: '14px 16px',
                  background: 'rgba(255,255,255,0.03)', borderRadius: 14,
                  border: '1px solid rgba(255,255,255,0.06)', textAlign: 'left',
                }}>
                <div style={{ width: 38, height: 38, borderRadius: 10, flexShrink: 0,
                  background: `${f.color}18`, color: f.color,
                  display: 'flex', alignItems: 'center', justifyContent: 'center' }}>{f.icon}</div>
                <p style={{ color: '#CBD5E1', fontSize: 13, fontWeight: 500 }}>{f.text}</p>
              </motion.div>
            ))}
          </div>

          {/* Stats */}
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5 }}
            style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 16 }}>
            {[
              { value: '500+',  label: 'Businesses' },
              { value: '180+',  label: 'Countries'  },
              { value: '10%',   label: 'Referral'   },
            ].map((s, i) => (
              <div key={i} style={{ textAlign: 'center', padding: '14px 8px',
                background: 'rgba(255,255,255,0.03)', borderRadius: 12,
                border: `1px solid ${P}20` }}>
                <p style={{ fontSize: 22, fontWeight: 900, color: P2, letterSpacing: -0.5, margin: '0 0 4px' }}>{s.value}</p>
                <p style={{ fontSize: 11, color: '#475569', margin: 0 }}>{s.label}</p>
              </div>
            ))}
          </motion.div>

        </div>
      </div>

      {/* ── RIGHT PANEL (Form) ── */}
      <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '32px 24px' }}>
        <motion.div initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }}
          style={{ width: '100%', maxWidth: 440 }}>

          {/* Mobile logo */}
          <div className="lg-hide" style={{ textAlign: 'center', marginBottom: 32 }}>
            <Link to="/" style={{ display: 'inline-flex', alignItems: 'center', gap: 10, textDecoration: 'none' }}>
              <KudiLogo size={36} />
              <span style={{ fontSize: 24, fontWeight: 900, color: '#fff', letterSpacing: -0.5 }}>
                Kudi<span style={{ color: P2 }}>bot</span>
              </span>
            </Link>
          </div>

          {/* Card */}
          <div style={{
            background: 'rgba(255,255,255,0.03)', borderRadius: 24,
            border: '1px solid rgba(255,255,255,0.08)', padding: '40px 36px',
          }}>

            <div style={{ marginBottom: 28 }}>
              <h1 style={{ fontSize: 28, fontWeight: 900, color: '#fff', margin: '0 0 6px', letterSpacing: -0.5 }}>
                {otpStep ? 'Verify it\'s you 🔐' : 'Welcome back 👋'}
              </h1>
              <p style={{ color: '#64748B', fontSize: 14, margin: 0 }}>
                {otpStep ? `Enter the 6-digit code sent to ${otpEmail}` : 'Sign in to your Kudibot account'}
              </p>
            </div>

            {otpStep ? (
              <form onSubmit={handleVerify} style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
                <div>
                  <label style={{ display: 'block', fontSize: 11, fontWeight: 700, color: '#64748B',
                    letterSpacing: 1, textTransform: 'uppercase', marginBottom: 8 }}>
                    Login Code
                  </label>
                  <input type="text" inputMode="numeric" maxLength={6} placeholder="______" autoFocus
                    value={otp}
                    onChange={e => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                    required
                    style={{
                      width: '100%', background: '#0D1117', border: '1px solid rgba(255,255,255,0.08)',
                      borderRadius: 12, padding: '14px', color: '#fff', fontSize: 24, fontWeight: 800,
                      letterSpacing: 12, textAlign: 'center', outline: 'none', boxSizing: 'border-box',
                    }}
                    onFocus={e => e.target.style.borderColor = `${P}60`}
                    onBlur={e => e.target.style.borderColor = 'rgba(255,255,255,0.08)'}
                  />
                </div>
                <motion.button whileHover={{ scale: 1.02, boxShadow: `0 0 32px ${P}40` }} whileTap={{ scale: 0.98 }}
                  type="submit" disabled={isLoading}
                  style={{
                    width: '100%', padding: '14px 0', borderRadius: 12, border: 'none', cursor: 'pointer',
                    background: `linear-gradient(135deg, ${P}, ${C})`, color: '#fff', fontWeight: 800, fontSize: 15,
                    display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, boxShadow: `0 0 24px ${P}30`,
                  }}>
                  {isLoading
                    ? <div style={{ width: 20, height: 20, border: '2px solid rgba(255,255,255,0.3)', borderTopColor: '#fff', borderRadius: '50%', animation: 'spin 0.7s linear infinite' }}/>
                    : <><span>Verify &amp; Sign In</span><ArrowRight size={16}/></>}
                </motion.button>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <button type="button" onClick={() => { setOtpStep(false); setOtp(''); }}
                    style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#64748B', fontSize: 13, fontWeight: 600 }}>
                    ← Back to sign in
                  </button>
                  <button type="button" onClick={handleResend} disabled={resendIn > 0}
                    style={{ background: 'none', border: 'none', cursor: resendIn > 0 ? 'default' : 'pointer',
                      color: resendIn > 0 ? '#334155' : P2, fontSize: 13, fontWeight: 700 }}>
                    {resendIn > 0 ? `Resend in ${resendIn}s` : 'Resend code'}
                  </button>
                </div>
              </form>
            ) : (
            <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>

              {/* Email */}
              <div>
                <label style={{ display: 'block', fontSize: 11, fontWeight: 700, color: '#64748B',
                  letterSpacing: 1, textTransform: 'uppercase', marginBottom: 8 }}>
                  Email Address
                </label>
                <div style={{ position: 'relative' }}>
                  <Mail size={15} style={{ position: 'absolute', left: 14, top: '50%', transform: 'translateY(-50%)', color: '#475569' }}/>
                  <input type="email" placeholder="you@example.com"
                    value={form.email}
                    onChange={e => setForm({ ...form, email: e.target.value })}
                    required
                    style={{
                      width: '100%', background: '#0D1117', border: '1px solid rgba(255,255,255,0.08)',
                      borderRadius: 12, padding: '13px 14px 13px 42px', color: '#fff', fontSize: 14,
                      outline: 'none', boxSizing: 'border-box', transition: 'border-color .2s',
                    }}
                    onFocus={e => e.target.style.borderColor = `${P}60`}
                    onBlur={e => e.target.style.borderColor = 'rgba(255,255,255,0.08)'}
                  />
                </div>
              </div>

              {/* Password */}
              <div>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
                  <label style={{ fontSize: 11, fontWeight: 700, color: '#64748B',
                    letterSpacing: 1, textTransform: 'uppercase' }}>
                    Password
                  </label>
                </div>
                <div style={{ position: 'relative' }}>
                  <Lock size={15} style={{ position: 'absolute', left: 14, top: '50%', transform: 'translateY(-50%)', color: '#475569' }}/>
                  <input type={showPass ? 'text' : 'password'} placeholder="••••••••"
                    value={form.password}
                    onChange={e => setForm({ ...form, password: e.target.value })}
                    required
                    style={{
                      width: '100%', background: '#0D1117', border: '1px solid rgba(255,255,255,0.08)',
                      borderRadius: 12, padding: '13px 44px 13px 42px', color: '#fff', fontSize: 14,
                      outline: 'none', boxSizing: 'border-box', transition: 'border-color .2s',
                    }}
                    onFocus={e => e.target.style.borderColor = `${P}60`}
                    onBlur={e => e.target.style.borderColor = 'rgba(255,255,255,0.08)'}
                  />
                  <button type="button" onClick={() => setShowPass(!showPass)}
                    style={{ position: 'absolute', right: 14, top: '50%', transform: 'translateY(-50%)',
                      background: 'none', border: 'none', cursor: 'pointer', color: '#475569', padding: 0 }}>
                    {showPass ? <EyeOff size={15}/> : <Eye size={15}/>}
                  </button>
                </div>
                <div style={{ textAlign: 'right', marginTop: 8 }}>
                  <Link to="/forgot-password" style={{ color: '#64748B', fontSize: 13, fontWeight: 600, textDecoration: 'none' }}>
                    Forgot password?
                  </Link>
                </div>
              </div>

              {/* Submit */}
              <motion.button whileHover={{ scale: 1.02, boxShadow: `0 0 32px ${P}40` }}
                whileTap={{ scale: 0.98 }}
                type="submit" disabled={isLoading}
                style={{
                  width: '100%', padding: '14px 0', borderRadius: 12, border: 'none', cursor: 'pointer',
                  background: `linear-gradient(135deg, ${P}, ${C})`,
                  color: '#fff', fontWeight: 800, fontSize: 15,
                  display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                  boxShadow: `0 0 24px ${P}30`, marginTop: 4,
                }}>
                {isLoading
                  ? <div style={{ width: 20, height: 20, border: '2px solid rgba(255,255,255,0.3)',
                      borderTopColor: '#fff', borderRadius: '50%', animation: 'spin 0.7s linear infinite' }}/>
                  : <><span>Sign In</span><ArrowRight size={16}/></>
                }
              </motion.button>

            </form>
            )}

            <div style={{ display: 'flex', alignItems: 'center', gap: 12, margin: '24px 0' }}>
              <div style={{ flex: 1, height: 1, background: 'rgba(255,255,255,0.06)' }}/>
              <span style={{ fontSize: 11, color: '#334155' }}>OR</span>
              <div style={{ flex: 1, height: 1, background: 'rgba(255,255,255,0.06)' }}/>
            </div>

            <p style={{ textAlign: 'center', color: '#64748B', fontSize: 14, margin: 0 }}>
              Don't have an account?{' '}
              <Link to="/register" style={{ color: P2, fontWeight: 700, textDecoration: 'none' }}>
                Create Account →
              </Link>
            </p>
          </div>

          {/* Trust strip */}
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 24, marginTop: 20 }}>
            {[
              [Shield, 'Secure'], [Globe, '180+ Countries'], [Zap, 'Instant Payouts'],
            ].map(([Icon, label], i) => (
              <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 5,
                fontSize: 11, color: '#334155' }}>
                <Icon size={12} color={P}/> {label}
              </div>
            ))}
          </div>

        </motion.div>
      </div>

      <style>{`
        @media (min-width: 1024px) { .lg-panel { display: flex !important; } .lg-hide { display: none !important; } }
        @keyframes spin { to { transform: rotate(360deg); } }
        input::placeholder { color: #334155; }
        select option { background: #0D1117; }
      `}</style>

    </div>
  );
}
